<template>
  <div class="queue-page">
    <AppSectionHero
      title="Review Queue"
      subtitle="Track each MOU from user submission through committee review and final admin decision."
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectStatusCard"
    />

    <CCard class="border-0 shadow-sm queue-card">
      <CCardBody class="p-4 p-lg-5">
        <div class="card-heading">
          <div>
            <div class="section-title">Current Queue</div>
            <div class="section-subtitle">Open incoming submissions, continue workflow routing, and finish final admin decisions from one place.</div>
          </div>
        </div>

        <div class="queue-controls">
          <div class="queue-search">
            <CIcon name="cil-search" />
            <input
              v-model="queueSearch"
              type="search"
              placeholder="Search agreements, partners, owners, or codes"
            >
          </div>
          <select v-model="activeQueueFilter" class="queue-select" aria-label="Filter review queue status">
            <option value="all">All in process</option>
            <option value="queued">Queued</option>
            <option value="in-review">Review</option>
            <option value="completed">Completed</option>
          </select>
          <select v-model.number="queuePageSize" class="queue-select" aria-label="Rows per page">
            <option :value="5">5 per page</option>
            <option :value="10">10 per page</option>
          </select>
        </div>

        <div v-if="searchedWorkflows.length" class="queue-summary">
          {{ queueShowingLabel }}
        </div>

        <div v-if="searchedWorkflows.length" class="queue-grid">
          <div
            v-for="item in pagedWorkflows"
            :key="item.id"
            class="queue-item-card"
            @click="openReview(item)"
          >
            <div class="queue-item-card__head">
              <div>
                <div class="queue-item-card__title">{{ item.title }}</div>
                <div class="queue-item-card__meta">{{ item.type }} · {{ item.partner }}</div>
              </div>
              <div class="queue-item-card__badges">
                <span class="status-pill" :class="statusClass(item.status)">
                  {{ statusText(item.status) }}
                </span>
                <span class="queue-stage-pill">{{ stageLabel(item) }}</span>
              </div>
            </div>

            <div class="queue-item-card__details">
              <div class="detail-row"><span>Document</span><strong>{{ item.documentCode || item.id }}</strong></div>
              <div class="detail-row"><span>Owner</span><strong>{{ item.createdBy }}</strong></div>
              <div class="detail-row"><span>Committee</span><strong>{{ committeeText(item) }}</strong></div>
              <div class="detail-row"><span>Progress</span><strong>{{ progressFor(item) }}%</strong></div>
            </div>

            <div class="queue-item-card__progress">
              <div class="row-progress__track">
                <div class="row-progress__fill" :style="{ width: progressFor(item) + '%' }" />
              </div>
            </div>

            <div class="queue-item-card__actions">
              <CButton size="sm" color="primary" class="queue-item-card__primary-action" @click.stop="openReview(item)">
                {{ primaryActionLabel(item) }}
              </CButton>
            </div>
          </div>
        </div>

        <div v-if="searchedWorkflows.length && totalQueuePages > 1" class="queue-pagination">
          <CButton
            size="sm"
            color="secondary"
            variant="outline"
            :disabled="queuePage === 1"
            @click="queuePage -= 1"
          >
            Previous
          </CButton>
          <span>Page {{ queuePage }} of {{ totalQueuePages }}</span>
          <CButton
            size="sm"
            color="primary"
            :disabled="queuePage === totalQueuePages"
            @click="queuePage += 1"
          >
            Next
          </CButton>
        </div>

        <div v-if="!searchedWorkflows.length" class="empty-assignment">
          No review items match the current queue filter.
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import Service from '@/service/api'
import { readPageCache, writePageCache } from '@/utils/page-cache'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'

const REVIEW_QUEUE_CACHE_KEY = 'admin-review-queue-page'
const REVIEW_QUEUE_CACHE_MAX_AGE = 5 * 60 * 1000

function getProgress(currentStep, status) {
  if (status === 'approved') return 100
  return Math.max(25, Math.min(Number(currentStep || 1), 4) * 25)
}

function getStageLabel(item) {
  if (item.status === 'approved') return 'Approved'
  if (item.status === 'revision' || item.status === 'rejected') return 'Returned for User Revision'
  if (isReadyForAdminFinalApproval(item)) return 'Waiting for approval'
  return {
    1: 'Draft Submitted',
    2: 'Committee Notified',
    3: 'Committee Review and Comments'
  }[Math.min(Number(item.currentStep || 1), 3)] || 'Draft Submitted'
}

function getWorkflowTimestamp(item) {
  const candidates = [
    item && item.updatedAt,
    item && item.submittedAt,
    item && item.createdAt,
    item && item.approvedAt
  ]

  for (const value of candidates) {
    const timestamp = new Date(value).getTime()
    if (!Number.isNaN(timestamp) && timestamp > 0) return timestamp
  }

  return 0
}

export default {
  name: 'ReviewQueuePage',
  components: {
    AppSectionHero
  },
  data() {
    return {
      workflows: [],
      activeQueueFilter: 'all',
      queueSearch: '',
      queuePage: 1,
      queuePageSize: 5
    }
  },
  computed: {
    queuedCount() {
      return this.workflows.filter(item => item.currentStep === 1 || item.currentStep === 2).length
    },
    inReviewCount() {
      return this.workflows.filter(item => {
        const currentStep = Number(item.currentStep || 1)
        return currentStep === 3 || (currentStep === 4 && item.status !== 'approved' && !isReadyForAdminFinalApproval(item))
      }).length
    },
    completedCount() {
      return this.workflows.filter(item => item.status === 'approved').length
    },
    filteredWorkflows() {
      // Only show items that are not approved (in processing) in the main queue
      if (this.activeQueueFilter === 'queued') {
        return this.workflows.filter(item => (item.currentStep === 1 || item.currentStep === 2) && item.status !== 'approved')
      }
      if (this.activeQueueFilter === 'in-review') {
        return this.workflows.filter(item => {
          const currentStep = Number(item.currentStep || 1)
          return (currentStep === 3 || (currentStep === 4 && item.status !== 'approved' && !isReadyForAdminFinalApproval(item)))
            && item.status !== 'approved'
        })
      }
      if (this.activeQueueFilter === 'completed') {
        return this.workflows.filter(item => item.status === 'approved')
      }
      // Default: only in-process (not approved)
      return this.workflows.filter(item => item.status !== 'approved')
    },
    searchedWorkflows() {
      const query = String(this.queueSearch || '').trim().toLowerCase()
      if (!query) return this.filteredWorkflows
      return this.filteredWorkflows.filter((item) => {
        return [
          item.title,
          item.type,
          item.partner,
          item.createdBy,
          item.documentCode,
          item.id,
          this.statusText(item.status),
          this.stageLabel(item)
        ].some((value) => String(value || '').toLowerCase().includes(query))
      })
    },
    totalQueuePages() {
      return Math.max(1, Math.ceil(this.searchedWorkflows.length / this.queuePageSize))
    },
    pagedWorkflows() {
      const start = (this.queuePage - 1) * this.queuePageSize
      return this.searchedWorkflows.slice(start, start + this.queuePageSize)
    },
    queueShowingLabel() {
      const total = this.searchedWorkflows.length
      const start = total ? ((this.queuePage - 1) * this.queuePageSize) + 1 : 0
      const end = Math.min(total, start + this.queuePageSize - 1)
      return `Showing ${start}-${end} of ${total} agreements`
    },
    heroStats() {
      return [
        {
          label: 'Queued',
          value: this.queuedCount,
          hint: 'New submissions entering review',
          icon: 'cil-list',
          iconClass: 'app-section-stat__icon--total',
          clickable: true,
          active: this.activeQueueFilter === 'queued'
        },
        {
          label: 'Review',
          value: this.inReviewCount,
          hint: 'In review of committee',
          icon: 'cil-hourglass',
          iconClass: 'app-section-stat__icon--attention',
          clickable: true,
          active: this.activeQueueFilter === 'in-review'
        },
        {
          label: 'Completed',
          value: this.completedCount,
          hint: 'Approved items finished in the workflow',
          icon: 'cil-check-circle',
          iconClass: 'app-section-stat__icon--active',
          clickable: true,
          active: this.activeQueueFilter === 'completed'
        }
      ]
    }
  },
  watch: {
    activeQueueFilter() {
      this.queuePage = 1
    },
    queueSearch() {
      this.queuePage = 1
    },
    queuePageSize() {
      this.queuePage = 1
    },
    searchedWorkflows() {
      if (this.queuePage > this.totalQueuePages) this.queuePage = this.totalQueuePages
    }
  },
  mounted() {
    this.loadCachedQueue()
    this.loadQueue(true)
    window.addEventListener('focus', this.handleWindowFocus)
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.handleWindowFocus)
  },
  methods: {
    handleWindowFocus() {
      this.loadQueue(true)
    },
    loadCachedQueue() {
      const cached = readPageCache(REVIEW_QUEUE_CACHE_KEY, REVIEW_QUEUE_CACHE_MAX_AGE)
      if (!Array.isArray(cached) || !cached.length) return
      this.workflows = cached
    },
    async loadQueue(forceRefresh = false) {
      try {
        const response = await Service.adminReview('queue', {}, { forceRefresh })
        const records = response && response.data && Array.isArray(response.data.data) ? response.data.data : []
        const nextWorkflows = records.slice().sort((left, right) => {
          const timeDiff = getWorkflowTimestamp(right) - getWorkflowTimestamp(left)
          if (timeDiff !== 0) return timeDiff
          return String(right.id || '').localeCompare(String(left.id || ''))
        })
        this.workflows = nextWorkflows
        writePageCache(REVIEW_QUEUE_CACHE_KEY, nextWorkflows)
      } catch (error) {
        this.workflows = []
      }
    },
    progressFor(item) {
      return getProgress(item.currentStep, item.status)
    },
    stageLabel(item) {
      return getStageLabel(item)
    },
    statusText(status) {
      return {
        draft: 'Drafted',
        pending: 'Review',
        'sent-to-admin': 'Waiting for approval',
        approved: 'Approved',
        rejected: 'Rejected',
        revision: 'Revision Required'
      }[status] || status
    },
    statusClass(status) {
      return {
        draft: 'status-pill--draft',
        pending: 'status-pill--pending',
        approved: 'status-pill--approved',
        rejected: 'status-pill--rejected',
        revision: 'status-pill--rejected'
      }[status] || 'status-pill--draft'
    },
    committeeText(item) {
      return item.committees && item.committees.length
        ? item.committees.join(', ')
        : 'Committee queue active'
    },
    primaryActionLabel(item) {
      if (!item) return 'Open Review'
      if (item.status === 'approved') return 'View Document'
      if (isReadyForAdminFinalApproval(item)) return 'Final Approval'
      if (Number(item.currentStep || 1) <= 4) return 'Open Review'
      return 'Monitor Review'
    },
    selectStatusCard(stat) {
      const label = String(stat && stat.label || '').toLowerCase()
      const nextFilter = label === 'queued'
        ? 'queued'
        : (label === 'in review' || label === 'review')
          ? 'in-review'
          : label === 'completed'
            ? 'completed'
            : 'all'
      this.activeQueueFilter = this.activeQueueFilter === nextFilter ? 'all' : nextFilter
    },
    openReview(item) {
      const id = item && item.id ? item.id : item
      if (!id) return
      const path = item && item.status !== 'approved' && isReadyForAdminFinalApproval(item)
        ? `/review-queue/${id}/final-approval`
        : `/review-queue/${id}/review`
      this.$router.push(path)
    }
  }
}
</script>

<style scoped>
.queue-page {
  min-height: 100vh;
  padding: 24px;
}

.queue-card {
  border-radius: 18px;
}

.card-heading {
  display: grid;
  gap: 12px;
  margin-bottom: 16px;
}

.queue-controls {
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr) 180px 150px;
  margin-bottom: 14px;
}

.queue-search {
  align-items: center;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 14px;
  display: flex;
  gap: 10px;
  padding: 0 14px;
}

.queue-search input,
.queue-select {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 14px;
  color: #344767;
  min-height: 44px;
  width: 100%;
}

.queue-search input {
  border: 0;
  outline: none;
}

.queue-select {
  padding: 0 12px;
}

.queue-summary {
  color: #718096;
  font-size: 0.88rem;
  font-weight: 700;
  margin-bottom: 14px;
}

.queue-pagination {
  align-items: center;
  color: #52606d;
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 18px;
}

.section-title {
  color: #344767;
  font-size: 1rem;
  font-weight: 700;
}

.section-subtitle {
  color: #718096;
  margin-top: 4px;
}

.queue-grid {
  display: grid;
  gap: 16px;
}

.queue-item-card {
  background: #fafafa;
  border: 1px solid #edf2f7;
  border-radius: 16px;
  cursor: pointer;
  padding: 18px;
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.queue-item-card:hover {
  border-color: #d9e2ec;
  box-shadow: 0 12px 24px rgba(44, 52, 71, 0.08);
  transform: translateY(-1px);
}

.queue-item-card__head,
.queue-item-card__badges,
.queue-item-card__actions {
  display: flex;
  gap: 10px;
}

.queue-item-card__head {
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 14px;
}

.queue-item-card__title {
  color: #2d3748;
  font-size: 1.05rem;
  font-weight: 700;
}

.queue-item-card__meta {
  color: #718096;
  margin-top: 4px;
}

.queue-item-card__badges {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.queue-item-card__actions {
  justify-content: flex-end;
  width: 100%;
}

.queue-item-card__primary-action {
  border-radius: 999px;
  box-shadow: 0 10px 24px rgba(245, 166, 35, 0.28);
  font-weight: 700;
  min-width: 140px;
  padding: 0.5rem 1rem;
}

.queue-stage-pill {
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  color: #475569;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  justify-content: center;
  min-width: 220px;
  padding: 6px 10px;
  text-align: center;
}

.queue-item-card__details {
  margin-bottom: 14px;
}

.detail-row {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  padding: 4px 0;
}

.detail-row span {
  color: #718096;
}

.detail-row strong {
  color: #2d3748;
  font-weight: 600;
  text-align: left;
}

.queue-item-card__progress {
  margin-bottom: 14px;
}

.row-progress__track {
  background: #edf2f7;
  border-radius: 999px;
  height: 10px;
  overflow: hidden;
  width: 100%;
}

.row-progress__fill {
  background: linear-gradient(90deg, #9e212f 0%, #f5a623 100%);
  border-radius: 999px;
  height: 100%;
}

.status-pill {
  align-items: center;
  border-radius: 999px;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  justify-content: center;
  min-width: 110px;
  padding: 7px 12px;
  text-align: center;
}

.status-pill--draft {
  background: #edf2f7;
  color: #4a5568;
}

.status-pill--pending {
  background: #fff7ed;
  color: #9a3412;
}

.status-pill--approved {
  background: #dcfce7;
  color: #166534;
}

.status-pill--rejected {
  background: #fee2e2;
  color: #991b1b;
}

.empty-assignment {
  background: #fafafa;
  border: 1px dashed #d9e2ec;
  border-radius: 16px;
  color: #718096;
  padding: 24px;
  text-align: center;
}

@media (max-width: 767px) {
  .queue-page {
    padding: 16px;
  }

  .queue-controls {
    grid-template-columns: 1fr;
  }

  .queue-item-card__head,
  .queue-item-card__actions,
  .detail-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
